<template>
  <div class="col">
    <div class="d-flex flex-column">
      <div class="wrapper_select">
        <img :src="getFavicon(picked)" class="favicon" />
        <select :value="picked" @change="onChange($event)">
          <option
            v-for="(geoItemOption, domain) in dataset"
            :key="domain"
            :value="domain"
          >
            {{ domain }}
          </option>
        </select>
      </div>
      <div
        v-if="dataset[picked] && Object.keys(dataset[picked]).length"
        class="wrapper_domain_content"
      >
        <WidgetListTitle
          class="topics"
          :firstName="$t('main.geo.widgetListTitle')"
          :secondName="$t('main.geo.widgetListValue')"
        />
        <template
          v-for="(value, country) in dataset[picked]"
          :key="'indicator' + country"
        >
          <div class="country">
            <Flag :country="country" />
            <span class="country">{{ country }}</span>
            <svg
              v-if="hasMultipleDomains(country)"
              class="pie-icon"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              @click="openPopup(country)"
            >
              <rect x="2" y="8" width="3" height="5" fill="#4F46E5" rx="0.5"/>
              <rect x="6" y="5" width="3" height="8" fill="#10B981" rx="0.5"/>
              <rect x="10" y="3" width="3" height="10" fill="#F59E0B" rx="0.5"/>
            </svg>
          </div>
          <Indicator :width="value.share" class="w-100" />
          <div class="traffic text-end">{{ value.share }}%</div>
        </template>
      </div>
      <div v-else class="no-data-message">
        —
      </div>
    </div>

    <!-- Popup -->
    <div v-if="showPopup" class="popup-overlay" @click="closePopup">
      <div class="popup-content" @click.stop>
        <div class="popup-header">
          <span class="popup-title">{{ selectedCountry }}</span>
          <div class="popup-header-right">
            <select
              v-if="popupDates.length > 1"
              class="popup-date-select"
              :value="popupDate"
              @change="onPopupDateChange($event)"
            >
              <option
                v-for="date of popupDates"
                :key="date"
                :value="date"
              >
                {{ getFormattedDate(date) }}
              </option>
            </select>
            <img :src="CloseIcon" class="close-icon" @click="closePopup" />
          </div>
        </div>
        <div class="popup-body" @mousemove="handleMouseMove">
          <canvas ref="pieChart" class="pie-chart-canvas"></canvas>
          <div
            v-if="legendTooltip.show"
            class="legend-tooltip"
            :style="{ top: legendTooltip.top, left: legendTooltip.left }"
          >
            {{ legendTooltip.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import getFavicon from '@/helpers/domains/getFavicon'
import Indicator from '@/components/widgets/widgets-helpers/Indicator.vue'
import WidgetError from '@/components/errors/WidgetError.vue'
import WidgetListTitle from '@/components/widgets/widgets-helpers/WidgetListTitle.vue'
import Flag from '@/components/shared/Flag.vue'
import CloseIcon from '@/assets/icons/base/close.svg'
import Chart from 'chart.js/auto'
import getColor from '@/helpers/store/getColor'
import getFormattedDate from '@/helpers/date/getFormattedDate'
import { cssVar, onThemeChange } from '@/helpers/base/theme'

export default {
  data() {
    return {
      CloseIcon,
      showPopup: false,
      selectedCountry: '',
      popupDate: null,
      pieChartInstance: null,
      legendTooltip: {
        show: false,
        text: '',
        top: '0px',
        left: '0px',
      },
    }
  },
  components: {
    Indicator,
    WidgetError,
    WidgetListTitle,
    Flag,
  },
  props: ['picked', 'mutation', 'dataset'],
  computed: {
    ...mapGetters(['domains']),
    ...mapState('geo', {
      geoData: state => state.data,
    }),
    ...mapState('traffic', {
      trafficData: state => state.data,
    }),
    ...mapState('geo', {
      geoActualDate: state => state.actualDate,
    }),
    popupDates() {
      // Collect all dates available across all domains in geo data
      const dates = new Set()
      this.geoData.forEach(d => {
        d.data?.forEach(entry => {
          if (entry.date) dates.add(entry.date)
        })
      })
      return [...dates].sort().reverse()
    },
  },
  methods: {
    getFavicon,
    getFormattedDate,
    onChange(event) {
      const option = event.target.options[event.target.selectedIndex]
      const value = option.getAttribute('value')
      this.mutation(value)
    },
    hasMultipleDomains(country) {
      if (Object.keys(this.dataset).length <= 1) return false

      let count = 0
      for (const domain in this.dataset) {
        if (this.dataset[domain][country]) {
          count++
        }
      }
      return count > 1
    },
    openPopup(country) {
      this.selectedCountry = country
      this.popupDate = this.geoActualDate
      this.showPopup = true
      this.$nextTick(() => {
        this.renderPieChart(country, this.popupDate)
      })
    },
    onPopupDateChange(event) {
      this.popupDate = event.target.value
      this.renderPieChart(this.selectedCountry, this.popupDate)
    },
    closePopup() {
      if (this.pieChartInstance) {
        this.pieChartInstance.destroy()
        this.pieChartInstance = null
      }
      this.legendTooltip.show = false
      this.showPopup = false
      this.selectedCountry = ''
    },
    handleMouseMove(event) {
      if (this.legendTooltip.show) {
        this.legendTooltip.top = (event.clientY + 10) + 'px'
        this.legendTooltip.left = (event.clientX + 10) + 'px'
      }
    },
    getDomainColor(domain) {
      // Use the same color as the line chart (by index in store domains)
      const storeIndex = this.domains.indexOf(domain)
      return storeIndex !== -1 ? getColor(storeIndex) : getColor(0)
    },
    getGeoShareForDate(domainName, country, date) {
      const domainGeo = this.geoData.find(d => d.domainName === domainName)
      if (!domainGeo) return null
      const entry = domainGeo.data?.find(e => e.date === date)
      return entry?.data?.[country]?.share ?? null
    },
    getTrafficForDate(domainName, date) {
      const trafficItem = this.trafficData.find(d => d.domainName === domainName)
      if (!trafficItem?.data) return null
      // Try exact date, otherwise closest earlier date
      if (trafficItem.data[date] != null) return trafficItem.data[date]
      // Find closest date
      const dates = Object.keys(trafficItem.data).sort()
      const closest = dates.filter(d => d <= date).pop()
      return closest ? trafficItem.data[closest] : null
    },
    renderPieChart(country, date) {
      if (!this.$refs.pieChart) return
      const ctx = this.$refs.pieChart.getContext('2d')
      if (!ctx) return

      const chartData = []
      const labels = []
      const backgroundColors = []
      let totalCountryTraffic = 0
      const domainTrafficData = []

      for (const domain of this.domains) {
        const share = this.getGeoShareForDate(domain, country, date)
        if (share == null) continue

        const visits = this.getTrafficForDate(domain, date)
        if (!visits) continue

        const absoluteTraffic = visits * (share / 100)
        totalCountryTraffic += absoluteTraffic

        domainTrafficData.push({
          domain,
          traffic: absoluteTraffic,
          share,
        })
      }

      // Порядок доменов фиксирован (как в this.domains), не сортируем

      // Формируем данные для графика с постоянными цветами для доменов
      domainTrafficData.forEach(item => {
        labels.push(item.domain)
        chartData.push(item.traffic)
        backgroundColors.push(this.getDomainColor(item.domain))
      })

      if (this.pieChartInstance) {
        this.pieChartInstance.destroy()
        this.pieChartInstance = null
      }

      const vm = this

      this.pieChartInstance = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: labels,
          datasets: [{
            data: chartData,
            backgroundColor: backgroundColors,
            borderWidth: 2,
            borderColor: cssVar('--chart-segment-border'),
            hoverBorderWidth: 4,
            hoverBorderColor: backgroundColors,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          animation: false,
          transitions: {
            active: {
              animation: {
                duration: 300,
                easing: 'easeInOutQuart',
              }
            }
          },
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                padding: 15,
                font: {
                  size: 12,
                  family: 'Manrope',
                },
                color: cssVar('--text-primary'),
                usePointStyle: true,
                pointStyle: 'circle',
              },
              onHover: (event, legendItem, legend) => {
                const nativeEvent = event.native
                if (nativeEvent && nativeEvent.target) {
                  nativeEvent.target.style.cursor = 'pointer'
                }
                const chart = legend.chart
                const index = legendItem.index

                // Устанавливаем активный элемент для отображения hover эффекта
                chart.setActiveElements([{
                  datasetIndex: 0,
                  index: index
                }])
                chart.update()

                // Показываем tooltip
                const value = chartData[index]
                const percentage = totalCountryTraffic > 0
                  ? ((value / totalCountryTraffic) * 100).toFixed(1)
                  : 0
                const formattedValue = Math.round(value).toLocaleString()

                vm.legendTooltip.text = `${labels[index]}: ${formattedValue} (${percentage}%)`
                vm.legendTooltip.show = true
              },
              onLeave: (event, legendItem, legend) => {
                const nativeEvent = event.native
                if (nativeEvent && nativeEvent.target) {
                  nativeEvent.target.style.cursor = 'default'
                }
                const chart = legend.chart

                // Убираем активные элементы
                chart.setActiveElements([])
                chart.update()

                // Скрываем tooltip
                vm.legendTooltip.show = false
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const label = context.label || ''
                  const value = context.parsed || 0
                  const percentage = totalCountryTraffic > 0
                    ? ((value / totalCountryTraffic) * 100).toFixed(1)
                    : 0

                  // Форматируем число (округляем и добавляем разделители тысяч)
                  const formattedValue = Math.round(value).toLocaleString()

                  return `${label}: ${formattedValue} (${percentage}%)`
                }
              }
            }
          },
          onHover: (event, activeElements) => {
            event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default'
          }
        }
      })
    },
  },
  mounted() {
    // при смене темы перекрашиваем границы секторов и легенду
    this.detachThemeListener = onThemeChange(() => {
      const chart = this.pieChartInstance
      if (!chart) return
      chart.data.datasets[0].borderColor = cssVar('--chart-segment-border')
      chart.options.plugins.legend.labels.color = cssVar('--text-primary')
      chart.update('none')
    })
  },
  beforeUnmount() {
    if (this.detachThemeListener) this.detachThemeListener()
    if (this.pieChartInstance) {
      this.pieChartInstance.destroy()
    }
  },
}
</script>

<style scoped>
.title_country {
  font-size: 13px;
  color: var(--text-secondary3);
  margin-bottom: 7px;
}

.point {
  position: relative;
  max-width: 110px;
}

.indicator {
  margin: 0;
}

.item {
  margin-bottom: 8px;
  margin-top: 8px;
  white-space: nowrap;
}

.wrapper_select {
  position: relative;
  margin: 10px 0 25px;
}
.favicon {
  width: 16px;
  height: 16px;
  top: 14px;
  left: 15px;
  position: absolute;
}

.traffic {
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;
  color: var(--text-primary);
}

.country {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pie-icon {
  transition: opacity 0.2s;
  flex-shrink: 0;
  cursor: pointer;
}

.pie-icon:hover {
  opacity: 0.7;
}

select {
  background: var(--bg-surface);
  color: var(--text-primary);
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  padding: 10px 40px 10px 40px;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-size: 14px;
  appearance: none;
  background-image: url('/arrow_gray.svg');
  background-size: 12px;
  width: 100%;

  background-repeat: no-repeat;
  background-position: center right 14px;
}

select:focus {
  outline: none;
}
select > option {
  color: var(--text-primary);
  border: none;
  font-size: 14px;
}

.wrapper_domain_content {
  display: grid;
  grid: min-content / max-content 1fr max-content;
  gap: 15px 20px;
  align-items: center;
}

.topics {
  grid-column: span 3;
}

/* Popup styles */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--overlay-color);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background: var(--bg-surface);
  border-radius: 8px;
  padding: 24px;
  box-shadow: var(--shadow-strong);
  max-width: 400px;
  width: 90%;
  position: relative;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.popup-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.popup-date-select {
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 13px;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
}

.popup-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.close-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.close-icon:hover {
  opacity: 1;
}

.popup-body {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pie-chart-canvas {
  max-width: 100%;
  max-height: 300px;
}

.legend-tooltip {
  position: fixed;
  background: var(--bg-surface);
  border: 1px solid var(--border-color-2);
  border-radius: 7px;
  padding: 8px 12px;
  font-size: 12px;
  color: var(--text-primary);
  pointer-events: none;
  z-index: 10000;
  box-shadow: var(--shadow-small);
  white-space: nowrap;
}

.no-data-message {
  color: var(--text-secondary3);
  font-size: 16px;
  text-align: center;
  padding: 30px 0;
}
</style>
